import { useEffect, useState } from "react";
import api, { API, getCompany } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Loader2, Sparkles, Trash2 } from "lucide-react";
import DateInput from "@/components/DateInput";
import ProjectSelect from "@/components/ProjectSelect";
import { fmtDate, todayISO } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";

const TYPE_LABEL = { weekly: "Haftalık", monthly: "Aylık", custom: "Özel" };

const iso = (d) => d.toISOString().slice(0, 10);
const range = (type) => {
  const t = new Date();
  if (type === "weekly") { const s = new Date(t); s.setDate(t.getDate() - 6); return [iso(s), todayISO()]; }
  return [iso(new Date(Date.UTC(t.getFullYear(), t.getMonth(), 1))), todayISO()];
};
const TYPES = [["weekly", "Haftalık"], ["monthly", "Aylık"], ["custom", "Özel"]];

async function streamReport(body, onDelta) {
  const res = await fetch(`${API}/site-logs/report/stream`, { method: "POST", headers: { "Content-Type": "application/json", "X-Company-Id": getCompany() }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Rapor üretilemedi");
  const reader = res.body.getReader(), dec = new TextDecoder();
  let buf = "", report = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const chunks = buf.split("\n\n"); buf = chunks.pop();
    for (const c of chunks) {
      if (!c.startsWith("data: ")) continue;
      const ev = JSON.parse(c.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
      if (ev.done) report = ev.report;
    }
  }
  return report;
}

export function SiteLogReport({ projects }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", period_type: "weekly", date_from: range("weekly")[0], date_to: todayISO() });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get(`/site-logs/reports${form.project_id ? `?project_id=${form.project_id}` : ""}`).then(r => setHistory(r.data));
  useEffect(() => { if (open) loadHistory(); }, [open, form.project_id]);

  const setType = (t) => { const [a, b] = t === "custom" ? [form.date_from, form.date_to] : range(t); setForm({ ...form, period_type: t, date_from: a, date_to: b }); };
  const run = async () => {
    if (!form.project_id) { toast.error("Proje seçin"); return; }
    setBusy(true); setText("");
    try { await streamReport(form, d => setText(t => t + d)); toast.success("Dönem raporu üretildi"); loadHistory(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Raporu silmek istiyor musunuz?")) return;
    try { await api.delete(`/site-logs/reports/${id}`); toast.success("Rapor silindi"); loadHistory(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-period-report-button"><FileText className="w-4 h-4 mr-1 text-amber-500" /> AI Dönem Raporu</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="period-report-dialog">
        <DialogHeader><DialogTitle>AI Dönem Raporu (Haftalık / Aylık)</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({ ...form, project_id: v })} includeAll={false} testid="report-project" /></div>
          <div className="col-span-2 flex gap-2">
            {TYPES.map(([k, l]) => (
              <Button key={k} size="sm" variant={form.period_type === k ? "default" : "outline"} onClick={() => setType(k)} data-testid={`report-type-${k}`}>{l}</Button>
            ))}
          </div>
          <div><Label>Başlangıç</Label><DateInput data-testid="report-date-from" value={form.date_from} onChange={e => setForm({ ...form, period_type: "custom", date_from: e.target.value })} /></div>
          <div><Label>Bitiş</Label><DateInput data-testid="report-date-to" value={form.date_to} onChange={e => setForm({ ...form, period_type: "custom", date_to: e.target.value })} /></div>
        </div>
        <Button onClick={run} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="generate-period-report-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} Rapor Üret
        </Button>
        {text && <ReportBody content={text} testid="period-report-output" />}
        <ReportHistory rows={history} onDelete={remove} />
      </DialogContent>
    </Dialog>
  );
}

function ReportBody({ content, testid }) {
  return <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4 text-sm" data-testid={testid}><Markdown>{content}</Markdown></div>;
}

function ReportHistory({ rows, onDelete }) {
  const [openId, setOpenId] = useState(null);
  if (!rows.length) return null;
  return (
    <div className="space-y-2" data-testid="period-report-history">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Geçmiş Raporlar</div>
      {rows.map(r => (
        <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`period-report-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <button className="text-left text-sm" onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`toggle-report-${r.id}`}>
              <span className="font-semibold">{TYPE_LABEL[r.period_type] || "Özel"} · {r.project_name}</span> · {fmtDate(r.date_from)} – {fmtDate(r.date_to)}
              <span className="text-muted-foreground"> · {r.log_count} gün · ort. {r.avg_workforce} işçi · {new Date(r.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</span>
            </button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)} data-testid={`delete-report-${r.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
          </div>
          {openId === r.id && <div className="mt-2"><ReportBody content={r.content} testid={`report-content-${r.id}`} /></div>}
        </div>
      ))}
    </div>
  );
}
